import { Component, OnDestroy, OnInit } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RoleService } from '../../../core/services/role/role.service';
import { AgentsService } from '../../../core/services/agents/agents.service';
import IIremboAgentProfile from '../../../core/models/agentProfile/profile-model';
import { map, Observable, Subscription } from 'rxjs';
import { IRole } from '@irembo-andela/irembogov3-common';
import { EUserRole } from '../../../core/enums/allowed-roles-enums';
import { UiModule } from '@irembo-andela/irembogov3-common';

@Component({
  selector: 'irembogov-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [UiModule],
})
export class DashboardComponent implements OnInit, OnDestroy {
  userProfile: KeycloakProfile | undefined;
  isLoggedIn: boolean | undefined;
  metabaseUrl: SafeResourceUrl = '';
  ts: any;
  activeRole!: IRole;
  activeScopeId!: string;
  extraDashboardUserContext: Record<string, any> = {};
  private subscription = new Subscription();
  selectedAgency: Record<string, unknown> | undefined;

  constructor(
    private keycloak: KeycloakService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private roleService: RoleService,
    private agentsService: AgentsService
  ) {}

  async ngOnInit() {
    this.isLoggedIn = await this.keycloak.isLoggedIn();

    if (this.isLoggedIn) {
      this.userProfile = await this.keycloak.loadUserProfile();

      // Subscribe to agency changes and load dashboard only when we have an agency
      this.subscription.add(
        this.roleService.selectedAgency$.subscribe(agency => {
          if (agency && this.userProfile?.id) {
            this.selectedAgency = agency;
            this.getUserProfile(this.userProfile.id);
          }
        })
      );
    }
  }

  getUserProfile(userId: string) {
    this.agentsService.getUserById(userId).subscribe({
      next: payload => {
        this.loadDashboard(payload.data);
      },
      error: err => {
        console.error('Error:', err);
        this.ts.show({ body: err.error.message, type: 'error' });
      },
    });
  }

  private loadDashboard(userProfile: IIremboAgentProfile) {
    const activeRole = this.roleService.currentRole.value as IRole;
    if (!activeRole?.code) {
      return;
    }

    // Find role and matching scope for current agency
    const userRole = userProfile.userRoles.find(
      role => role.name === activeRole.code
    );

    if (!userRole) {
      return;
    }

    const roleScope = userRole.scopes.find(
      scope => scope.scopeId === this.selectedAgency?.['value']
    );

    if (!roleScope) {
      return;
    }

    this.loadVersion2Dashboard(roleScope.scopeId, activeRole, userProfile.id);
  }

  // Define the map of roles to their specific API loaders
  private roleLoaders: Record<
    string,
    (scopeId: string, userId: string) => Observable<Record<string, any>>
  > = {
    [EUserRole.ROLE_AGENT]: (_, userId) =>
      this.agentsService
        .getAgentByUserId(userId)
        .pipe(map(res => ({ agentIdNumber: res.data.agentIdNumber }))),
    [EUserRole.ROLE_AGENCY_ADMIN]: (scopeId, _) =>
      this.agentsService
        .getAgencyInfoById(scopeId)
        .pipe(map(res => ({ agentCodePrefix: res.data.agentCodePrefix }))),
  };

  private loadVersion2Dashboard(
    scopeId: string,
    activeRole: IRole,
    userId: string
  ): void {
    const loader = this.roleLoaders[activeRole.code];

    if (!loader) {
      return;
    }

    this.activeRole = activeRole;
    this.activeScopeId = scopeId;

    this.subscription.add(
      loader(scopeId, userId).subscribe({
        next: contextPayload => {
          this.extraDashboardUserContext = contextPayload;
        },
        error: () => {
          this.ts.show({
            body: 'Failed to load dashboard context details',
            type: 'error',
          });
        },
      })
    );
  }

  private loadVersion1Dashboard(roleCode: string, scopeId: string): void {
    this.agentsService
      .getMetabaseEmbedUrl(roleCode, scopeId)
      .pipe(
        map(response => {
          const url = response.data;
          // Skip invalid URLs
          if (!url || url === 'null' || url.startsWith('null/')) {
            return null;
          }
          return this.sanitizer.bypassSecurityTrustResourceUrl(url);
        })
      )
      .subscribe({
        next: url => {
          if (url) {
            this.metabaseUrl = url;
          }
        },
        error: err => {
          console.error('Error loading dashboard:', err);
          this.ts.show({ body: 'Failed to load dashboard', type: 'error' });
        },
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
